import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import AdminHeader from '../../components/admin/AdminHeader';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { adminAPI } from '../../lib/api';

const STATUS_COLORS = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  PROCESSING: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-800'
};

const PROVIDER_LABELS = {
  MPESA_DARAJA: 'Daraja',
  FAMILY_BANK: 'Family Bank',
  NCBA_TILL: 'M-Pesa'
};

const PaymentsPage = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [queryingId, setQueryingId] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalPayments: 0
  });

  const fetchPayments = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      const res = await adminAPI.getPayments(params);
      if (res?.success) {
        setPayments(res.data.payments || []);
        setPagination(res.data.pagination || pagination);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments(pagination.currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, pagination.currentPage]);

  const handleQuery = async (paymentId) => {
    setQueryingId(paymentId);
    try {
      const res = await adminAPI.queryPayment(paymentId);
      if (res?.success) {
        const query = res.data.query || {};
        const resultDesc = query.ResultDesc || query.message || query.error;
        const status = res.data.payment?.status || 'updated';
        if (resultDesc) {
          toast.success(`${status}: ${resultDesc}`, { duration: 6000 });
        } else {
          toast.success(`Status: ${status}`);
        }
        await fetchPayments(pagination.currentPage);
      }
    } catch (err) {
      toast.error(err.message || 'Query failed');
    } finally {
      setQueryingId(null);
    }
  };

  const formatAmount = (amount) => `Ksh ${Number(amount || 0).toLocaleString('en-KE')}`;

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="lg:pl-72">
        <AdminHeader onMenuClick={() => setSidebarOpen(true)} title="Payments" />

        <main className="p-6">
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
              <p className="text-sm text-gray-600 mt-1">
                {pagination.totalPayments} transaction{pagination.totalPayments !== 1 ? 's' : ''}
              </p>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination((p) => ({ ...p, currentPage: 1 }));
              }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">All statuses</option>
              <option value="PENDING">Pending</option>
              <option value="PROCESSING">Processing</option>
              <option value="COMPLETED">Completed</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="p-12 text-center text-gray-500">Loading payments…</div>
            ) : payments.length === 0 ? (
              <div className="p-12 text-center text-gray-500">No payments found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Bill ref</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Event</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Buyer</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Provider</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {payments.map((p) => (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-xs font-mono text-gray-700 max-w-[140px] truncate" title={p.billRef}>
                          {p.billRef || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 max-w-[160px] truncate">
                          {p.event?.title || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div className="truncate max-w-[140px]">{p.user?.name || p.phoneNumber}</div>
                          <div className="text-xs text-gray-400 truncate">{p.user?.email}</div>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{formatAmount(p.amount)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {PROVIDER_LABELS[p.paymentProvider] || p.paymentProvider || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-800'}`}>
                            {p.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {new Date(p.createdAt).toLocaleString('en-KE')}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {['PENDING', 'PROCESSING', 'FAILED'].includes(p.status)
                            && p.paymentProvider !== 'FAMILY_BANK'
                            && p.checkoutRequestId && (
                            <button
                              type="button"
                              onClick={() => handleQuery(p.id)}
                              disabled={queryingId === p.id}
                              className="text-sm text-orange-600 hover:text-orange-800 font-medium disabled:opacity-50"
                            >
                              {queryingId === p.id ? 'Querying…' : 'Query STK'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {pagination.totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                <button
                  type="button"
                  disabled={pagination.currentPage <= 1}
                  onClick={() => setPagination((p) => ({ ...p, currentPage: p.currentPage - 1 }))}
                  className="text-sm text-gray-600 disabled:opacity-40"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <button
                  type="button"
                  disabled={pagination.currentPage >= pagination.totalPages}
                  onClick={() => setPagination((p) => ({ ...p, currentPage: p.currentPage + 1 }))}
                  className="text-sm text-gray-600 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default PaymentsPage;
